<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { isBoolean } from 'lodash';
import { CustomFieldType, type ICustomField } from '@nova/core';
import CustomFieldBool from '@satellite/components/custom_forms/field_types/CustomFieldBool.vue';
import util from '@satellite/plugins/util';

/**
 * Adapts satellite's Yes / No custom field to Luna's form contract: BasicForm validates through
 * `rules`, `validationIterator` and `update:error`, so the rules run here and their messages are
 * handed to the field.
 *
 * `required` is deliberately not a prop: BasicForm always binds it (false here), and its generic
 * rule rejects "No". `isRequired` carries the real flag instead.
 */
defineOptions({ inheritAttrs: false });

const {
  modelValue = null,
  label = '',
  hint = null,
  fieldName = null,
  testId,
  rules = [],
  disabled = false,
  validationIterator = 0,
  externalError = null,
  shouldValidate = false,
  isRequired = false
} = defineProps<{
  /** Any type is accepted: a stored value that is not a boolean reads as unanswered. */
  modelValue?: unknown;
  label?: string;
  hint?: string | null;
  fieldName?: string | null;
  testId: string;
  rules?: Array<(value: unknown) => boolean | string>;
  disabled?: boolean;
  /** Changing it validates the field, however it was filled in. */
  validationIterator?: number;
  externalError?: string | null;
  shouldValidate?: boolean;
  /** A required answer cannot be cleared once picked. */
  isRequired?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean | null];
  'update:error': [hasError: boolean];
  'clear-external-error': [fieldName: string | null];
}>();

const lazyValue = ref<unknown>(modelValue);
const errorMessages = ref<string[]>([]);

const answer = computed(() => (isBoolean(lazyValue.value) ? lazyValue.value : null));
const hasError = computed(() => errorMessages.value.length > 0);

const field = computed<ICustomField>(() => ({
  name: fieldName ?? testId,
  type: CustomFieldType.Bool,
  label,
  description: hint ?? undefined,
  required: isRequired
}));

function validate(force = false) {
  if (!force && !shouldValidate) {
    return;
  }
  emit('clear-external-error', fieldName);
  const messages: string[] = util.validateValuesAgainstRules(answer.value, rules) ?? [];
  errorMessages.value = [...new Set(messages)];
}

function handleChange(value: unknown) {
  const nextAnswer = isBoolean(value) ? value : null;
  lazyValue.value = nextAnswer;
  emit('update:modelValue', nextAnswer);
}

watch(
  () => modelValue,
  value => {
    lazyValue.value = value;
  }
);
watch(
  () => externalError,
  (value, previous) => {
    const remaining = errorMessages.value.filter(message => message !== previous);
    errorMessages.value = value ? [...new Set([...remaining, value])] : remaining;
  },
  { immediate: true }
);
watch(lazyValue, () => nextTick(() => validate()));
watch(
  () => rules,
  () => validate(),
  { deep: true }
);
watch(
  () => validationIterator,
  () => validate(true)
);
watch(
  hasError,
  value => {
    if (shouldValidate) {
      emit('update:error', value);
    }
  },
  { immediate: true }
);
</script>

<template>
  <custom-field-bool
    class="yes-no-toggle"
    :field="field"
    :model-value="answer"
    :required="isRequired"
    :read-only="disabled"
    :test-id="testId"
    :error-messages="errorMessages"
    @update:model-value="handleChange" />
</template>
